<script setup lang="ts">
import { useImpactStore } from "~/stores/impact";
import RTImpactContainer from "~/components/RTImpact/RTImpactContainer/RTImpactContainer.vue";
import type { IImpactAllStates } from "~/types/api";
import type { IChartMode } from "~/components/common/types";
import ImpactLoading from "~/components/RTImpact/Impact/ImpactLoading.vue";
import { Alert } from "@frontiers-vue3/public-pages";

const main = useMain();
const researchTopicId = main.currentResearchTopic.id;
const {
  fetchGeolocations,
  fetchTopCountries,
  fetchTopReferrers,
  hasHistogramsInfo,
  fetchImpactV3Histogram,
  getTotalViews,
  geolocations,
  topCountries,
  topReferrers
} = useImpactStore();
const terminology = useTerminologyStore();

const metaData = usePageMetaInfo().getResearchTopicDetailsPageMetaInfo(
  main.currentResearchTopic,
  "/impact"
);

useHead(() => metaData);

const totalViews = ref<number>(0);
const isImpactFetching = ref<boolean>(true);
const initialChartMode = ref<IChartMode>("spline");
const impactFetchError = useState<string | undefined>(
  "impactFetchError",
  () => undefined
);

const getImpactViewsData = async (): Promise<void> => {
  const hasHistograms = hasHistogramsInfo();
  if (!hasHistograms) {
    try {
      await fetchImpactV3Histogram(main.currentResearchTopic.id);
    } catch (error) {
      console.error("Error fetching impact data. Error:", error);
      impactFetchError.value = error as string;
    }
  }
  totalViews.value = getTotalViews();
};

const demographicsLoading = ref<IImpactAllStates>({
  countries: !topCountries,
  geolocations: !geolocations,
  referrers: !topReferrers
});

const demographicsErrors = ref<IImpactAllStates>({
  countries: false,
  geolocations: false,
  referrers: false
});

const getDemographicData = async (): Promise<void> => {
  !geolocations &&
    fetchGeolocations(researchTopicId)
      .catch(() => (demographicsErrors.value.geolocations = true))
      .then(() => (demographicsLoading.value.geolocations = false));

  !topCountries &&
    fetchTopCountries(researchTopicId)
      .catch(() => (demographicsErrors.value.countries = true))
      .then(() => (demographicsLoading.value.countries = false));

  !topReferrers &&
    fetchTopReferrers(researchTopicId)
      .catch(() => (demographicsErrors.value.referrers = true))
      .then(() => (demographicsLoading.value.referrers = false));
};

const impactDataAvailable = computed((): boolean => {
  const {
    geolocations: computedGeolocations,
    topCountries: computedTopCountries,
    topReferrers: computedTopReferrers
  } = useImpactStore();

  return !!(
    totalViews.value ||
    computedGeolocations?.length ||
    computedTopCountries?.length ||
    computedTopReferrers?.length
  );
});

const displayImpactAlertMessage = computed((): boolean => {
  return (
    !isImpactFetching.value &&
    !impactDataAvailable.value &&
    !impactFetchError.value
  );
});

const displayImpactContainer = computed((): boolean => {
  return (
    !isImpactFetching.value &&
    impactDataAvailable.value &&
    !impactFetchError.value
  );
});

Promise.all([getImpactViewsData(), getDemographicData()])
  .then(() => {
    if (!totalViews.value) initialChartMode.value = "map";
  })
  .finally(() => {
    isImpactFetching.value = false;
  });
</script>

<template>
  <section class="RTImpact">
    <h2 class="Section__title">Impact</h2>
    <template v-if="isImpactFetching">
      <ImpactLoading loading-message="" />
    </template>
    <div v-if="!!impactFetchError && !isImpactFetching" class="RTImpact__Error">
      <Alert type="error" message="Unable to retrieve component data." />
    </div>
    <RTImpactContainer
      v-else-if="displayImpactContainer"
      :initial-chart-mode="initialChartMode"
      :demographics-errors="demographicsErrors"
      :demographics-loading="demographicsLoading" />
    <AlertMessage
      v-if="displayImpactAlertMessage"
      :message="`The ${terminology.getTerm(
        'researchTopic'
      )} has been launched recently. Impact data will be published on this page as soon as it becomes available.`" />
  </section>
</template>

<style lang="scss">
.RTImpact {
  @include tab-content-width;

  &__Title {
    @include tab-title-size;
  }
}
</style>
